/**
 * SourceBadge — renders a public-source attribution chip ("Sourced from PubMed").
 * Every enriched fact in Aurora is traceable; this is the UI of that promise.
 */
import type { SourceSystem } from '@aurora/shared';

const LABELS: Record<SourceSystem, string> = {
  pubmed: 'PubMed',
  nih_reporter: 'NIH RePORTER',
  orcid: 'ORCID',
  conference: 'Conference',
  lab_page: 'Lab page',
  crossref: 'Crossref',
  manual: 'Manual',
  derived: 'Derived (AI)',
};

export function SourceBadge({
  source,
  url,
}: {
  source: SourceSystem;
  url?: string | null;
}): JSX.Element {
  const label = LABELS[source] ?? source;
  const className = `source-badge source-${source}`;
  if (url) {
    return (
      <a className={className} href={url} target="_blank" rel="noreferrer">
        {label}
      </a>
    );
  }
  return <span className={className}>{label}</span>;
}

/** A row of de-duplicated source badges for a set of attributions. */
export function SourceBadgeRow({
  sources,
}: {
  sources: SourceSystem[];
}): JSX.Element | null {
  const unique = Array.from(new Set(sources));
  if (unique.length === 0) return null;
  return (
    <span className="source-badge-row">
      {unique.map((s) => (
        <SourceBadge key={s} source={s} />
      ))}
    </span>
  );
}
